import { AttachFile, Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  usePresignSupportAttachmentMutation,
  useSubmitSupportRequestMutation,
} from "features/auth/auth";
import type { User } from "types/interfaces/user";
import { CALIBRATE_FONT_FAMILY_ACCENT } from "components/hoc/Theme";
import CustomScrollbars from "components/CustomScrollbars";
import FieldLabel from "components/common/FieldLabel";

interface ContactSupportModalProps {
  open: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
}

interface FormData {
  name: string;
  email: string;
  issueType: string;
  description: string;
}

const ISSUE_TYPES = [
  "Unexpected Results",
  "Access Issue",
  "Product Feedback",
  "Other",
];

const MAX_ATTACHMENTS = 5;
const MAX_TOTAL_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ATTACHMENT_ACCEPT =
  "image/*,.pdf,.csv,.xlsx,.xls,.doc,.docx,.txt";

const EMPTY_FORM: FormData = {
  name: "",
  email: "",
  issueType: "",
  description: "",
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const helperSx = {
  "& .MuiFormHelperText-root": {
    fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
  },
} as const;

export default function ContactSupportModal({
  open,
  onClose,
  onSubmitted,
}: ContactSupportModalProps) {
  const currentUser = useSelector(
    (state: { auth: { user: User | null } }) => state.auth.user,
  );
  const [submitSupportRequest] = useSubmitSupportRequestMutation();
  const [presignSupportAttachment] = usePresignSupportAttachmentMutation();

  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setFormData({ ...EMPTY_FORM, email: currentUser?.email || "" });
      setAttachments([]);
      setSubmitError(null);
    }
  }, [open, currentUser?.email]);

  const totalAttachmentBytes = attachments.reduce((sum, f) => sum + f.size, 0);
  const attachmentError =
    attachments.length > MAX_ATTACHMENTS
      ? `You can attach at most ${MAX_ATTACHMENTS} files.`
      : totalAttachmentBytes > MAX_TOTAL_ATTACHMENT_BYTES
        ? "Attachments are too large. Total size must be 5 MB or less."
        : null;

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length) setAttachments((prev) => [...prev, ...picked]);
    e.target.value = "";
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const attachmentKeys: string[] = [];
      for (const file of attachments) {
        const presign = await presignSupportAttachment({
          file_name: file.name,
          content_type: file.type || "application/octet-stream",
        }).unwrap();
        const uploadResponse = await fetch(presign.upload_url, {
          method: "PUT",
          headers: presign.headers,
          body: file,
        });
        if (!uploadResponse.ok) {
          throw new Error(`Upload failed with status ${uploadResponse.status}`);
        }
        attachmentKeys.push(presign.key);
      }

      await submitSupportRequest({
        name: formData.name.trim(),
        email: formData.email.trim(),
        issue_type: formData.issueType,
        description: formData.description.trim(),
        attachment_keys: attachmentKeys,
      }).unwrap();
      onSubmitted?.();
      onClose();
    } catch {
      setSubmitError("Could not send your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitDisabled =
    submitting ||
    !formData.name.trim() ||
    !formData.email.trim() ||
    !formData.issueType ||
    !formData.description.trim() ||
    !!attachmentError;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { width: "540px", maxWidth: "90vw", m: 2 } }}
    >
      <DialogTitle
        sx={{
          fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
          fontWeight: 700,
          backgroundColor: "#185059",
          color: "#FFFFFF",
          textAlign: "center",
          py: "20px",
          position: "relative",
        }}
      >
        Contact the Support Team
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { color: "#FFFFFF" },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", overflow: "hidden" }}>
        <CustomScrollbars
          sx={{ width: "100%" }}
          trackOffsetTop={8}
          trackOffsetBottom={8}
        >
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                textAlign: "center",
                color: "text.secondary",
                mt: 0.5,
                mb: 0.5,
                pb: 1.5,
                borderBottom: "1px solid #CAC4D0",
              }}
            >
              Fill in the details below and a member of the CalibrateHCM Support
              Team will reach out to assist you.
            </Typography>

            <Box>
              <FieldLabel required>Name</FieldLabel>
              <TextField
                fullWidth
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Enter your name"
                sx={helperSx}
              />
            </Box>

            <Box>
              <FieldLabel required>Email Address</FieldLabel>
              <TextField
                fullWidth
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="Email"
                helperText={
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                      color: "#31819F",
                      fontWeight: 600,
                    }}
                  >
                    Auto-populated from your user profile.
                  </Typography>
                }
                sx={helperSx}
              />
            </Box>

            <Box>
              <FieldLabel required>Issue Type</FieldLabel>
              <TextField
                select
                fullWidth
                value={formData.issueType}
                onChange={(e) =>
                  setFormData({ ...formData, issueType: e.target.value })
                }
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) =>
                    selected ? (
                      (selected as string)
                    ) : (
                      <Box component="span" sx={{ color: "text.disabled" }}>
                        Select an issue type
                      </Box>
                    ),
                }}
                sx={helperSx}
              >
                {ISSUE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box>
              <FieldLabel required>
                Describe the issue or provide feedback
              </FieldLabel>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Tell us what you were trying to do and what happened instead."
                sx={helperSx}
              />
            </Box>

            <Box>
              <FieldLabel>Attachments</FieldLabel>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                hidden
                onChange={handleFilesSelected}
              />
              <Button
                variant="outlined"
                startIcon={<AttachFile />}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                  textTransform: "none",
                  borderColor: "#185059",
                  color: "#185059",
                  "&:hover": { borderColor: "#185059", backgroundColor: "rgba(24,80,89,0.04)" },
                }}
              >
                Attach files
              </Button>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                  color: "text.secondary",
                }}
              >
                Up to {MAX_ATTACHMENTS} files, 5 MB total.
              </Typography>

              {attachments.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {attachments.map((file, i) => (
                    <Chip
                      key={`${file.name}-${i}`}
                      label={`${file.name} (${formatBytes(file.size)})`}
                      onDelete={() => removeAttachment(i)}
                      sx={{ fontFamily: CALIBRATE_FONT_FAMILY_ACCENT, maxWidth: "100%" }}
                    />
                  ))}
                </Box>
              )}

              {attachmentError && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.75,
                    fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                  }}
                  color="error"
                >
                  {attachmentError}
                </Typography>
              )}
            </Box>

            {submitError && (
              <Typography
                variant="body2"
                sx={{ fontFamily: CALIBRATE_FONT_FAMILY_ACCENT }}
                color="error"
              >
                {submitError}
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              sx={{
                fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                mt: 1,
                backgroundColor: "#185059",
                color: "#FFFFFF",
                textTransform: "uppercase",
                fontWeight: 500,
                py: 1.5,
                boxShadow: 2,
                "&:hover": {
                  backgroundColor: "#257885",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.25)",
                },
                "&:active": {
                  backgroundColor: "#185059",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
                },
                "&:disabled": {
                  backgroundColor: "rgba(24, 80, 89, 0.5)",
                  color: "rgba(255,255,255,0.7)",
                },
              }}
            >
              {submitting ? "Submitting..." : "Submit Request"}
            </Button>
          </Box>
        </CustomScrollbars>
      </DialogContent>
    </Dialog>
  );
}
